import {
  endpointsList,
  getWorkspaceDocumentDetailAccessMode,
  getWorkspaceDocumentDetailEndpoints,
} from '@/helpers';
import {
  DOCUMENT_OID_PARAM,
  PROJECT_ID_PARAM,
  PROJECT_TYPE_PARAM,
  Route,
  routeParams,
  SUPPLIER_ID_PARAM,
  UPLOAD_REFERENCE_NUMBER_PARAM,
  WORKSPACE_DOCUMENT_ID_PARAM,
  WORKSPACE_GROUP_ID_PARAM,
  WORKSPACE_ID_PARAM,
} from '@/routes';
import { ProjectDataService, SuppliersService } from '@/services';
import { DocumentsService } from '@/services/documents/DocumentsService';
import { DigitalizationProjectDetailsTab } from '@/views/DigitalizationProjectDetails/DigitalizationProjectDetails.types';
import { ProjectDetailsTab } from '@/views/ProjectDetails/ProjectDetails.types';
import axios from 'axios';
import {
  IWorkspaceDetailsDto,
  IWorkspaceDocumentMetadataResponseDto,
  ProjectType,
  TITLE_METADATA_FIELD,
} from 'types';
import { ComposerTranslation, VueI18n } from 'vue-i18n';
import {
  RouteLocationNormalizedGeneric,
  RouteLocationRaw,
  Router,
} from 'vue-router';
import { TABLE_NAMESPACE as DIGITALIZATION_DOCUMENTS_TABLE_NAMESPACE } from '../DigitalizationDocumentsOverview/constants';
import { TABLE_NAMESPACE as SUPPLIER_DOCUMENTS_TABLE_NAMESPACE } from '../FilesOverviewTable/constants';
import { namespaceQueryParams } from '../TableWrapper';
import { i18n } from '@/vue';

export type BreadcrumbHierarchyItem = {
  route: Route;
  children?: BreadcrumbHierarchyItem[];
  getLabel?: (route: HistoryItem) => Promise<string | null> | string | null;
  getRouteParams?: (route: HistoryItem) => RouteLocationRaw | null;
  getAdditionalPrecedingBreadcrumb?: (
    route: HistoryItem,
    t?: ComposerTranslation,
    historyPath?: string,
    router?: Router,
  ) => AdditionalPrecedingBreadcrumb | null;
};
export type AdditionalPrecedingBreadcrumb = BreadcrumbHierarchyItem & {
  children?: undefined;
};

export type HistoryItem = Omit<RouteLocationNormalizedGeneric, 'matched'>;

export const breadcrumbHierarchy: BreadcrumbHierarchyItem[] = [
  {
    route: Route.PROJECTS_LIST,
    getRouteParams: () => ({
      name: Route.PROJECTS_LIST,
    }),
    children: [
      {
        route: Route.CREATE_PROJECT,
      },
      {
        route: Route.PROJECT_DETAILS,
        getLabel: async (route) => {
          const projectId = getParamFromRoute(route, PROJECT_ID_PARAM);
          if (!projectId) return null;
          return await ProjectDataService.getSupplierProjectTitleById(
            projectId,
          );
        },
        getRouteParams: (route) => {
          const projectId = getParamFromRoute(route, PROJECT_ID_PARAM);
          if (!projectId) return null;
          const paramsConfig = routeParams[Route.PROJECT_DETAILS];
          return {
            name: Route.PROJECT_DETAILS,
            params: {
              [paramsConfig[PROJECT_ID_PARAM]]: projectId,
            },
          };
        },
        children: [
          {
            route: Route.PROJECT_SUPPLIER_DETAILS,
            getLabel: async (route) => {
              const supplierId = getParamFromRoute(route, SUPPLIER_ID_PARAM);
              if (!supplierId) return null;
              return await SuppliersService.getSupplierName(supplierId);
            },
            getRouteParams: (route) => {
              const projectId = getParamFromRoute(route, PROJECT_ID_PARAM);
              const supplierId = getParamFromRoute(route, SUPPLIER_ID_PARAM);
              if (!supplierId || !projectId) return null;
              const paramsConfig = routeParams[Route.PROJECT_SUPPLIER_DETAILS];
              return {
                name: Route.PROJECT_SUPPLIER_DETAILS,
                params: {
                  [paramsConfig[PROJECT_ID_PARAM]]: projectId,
                  [paramsConfig[SUPPLIER_ID_PARAM]]: supplierId,
                },
              };
            },
            children: [
              {
                route: Route.CONTENT_TYPE_CONFIGURATION,
              },
              {
                route: Route.CHECK_BOT_CONFIGURATOR,
              },
            ],
          },
          {
            route: Route.DOCUMENT_DETAIL_VIEW,
            getLabel: async (route) => {
              const documentOid = getParamFromRoute(route, DOCUMENT_OID_PARAM);
              if (!documentOid) return null;
              return await DocumentsService.getSupplierDocumentTitle(
                documentOid,
              );
            },
            getAdditionalPrecedingBreadcrumb: (
              route,
              t,
              historyPath,
              router,
            ) => {
              const uploadReferenceNumber = getParamFromRoute(
                route,
                UPLOAD_REFERENCE_NUMBER_PARAM,
              );
              const projectId = getParamFromRoute(route, PROJECT_ID_PARAM);
              if (!uploadReferenceNumber || !projectId || !t) return null;
              return {
                route: Route.PROJECT_DETAILS,
                getLabel: () =>
                  `${t(
                    'breadCrumb.uploadReferenceNumber',
                  )} ${uploadReferenceNumber}`,
                getRouteParams: () => {
                  const paramsFromHistory =
                    historyPath && router ? router.resolve(historyPath) : null;
                  const paramsConfig = routeParams[Route.PROJECT_DETAILS];
                  return {
                    name: Route.PROJECT_DETAILS,
                    params: {
                      ...(paramsFromHistory ? paramsFromHistory.params : {}),
                      [paramsConfig[PROJECT_ID_PARAM]]: projectId,
                      [paramsConfig.tab]: ProjectDetailsTab.DOCUMENTS,
                    },
                    query: {
                      ...(paramsFromHistory ? paramsFromHistory.query : {}),
                      ...namespaceQueryParams(
                        {
                          referenceNumber: uploadReferenceNumber,
                        },
                        SUPPLIER_DOCUMENTS_TABLE_NAMESPACE,
                      ),
                    },
                  };
                },
              };
            },
          },
          {
            route: Route.DOCUMENT_UPLOAD,
          },
        ],
      },
    ],
  },
  {
    route: Route.DIGITALIZATION_PROJECTS_LIST,
    getRouteParams: () => ({
      name: Route.DIGITALIZATION_PROJECTS_LIST,
    }),
    children: [
      {
        route: Route.CREATE_DIGITALIZATION_PROJECT,
      },
      {
        route: Route.DIGITALIZATION_PROJECT_DETAILS,
        getLabel: async (route) => {
          const projectId = getParamFromRoute(route, PROJECT_ID_PARAM);
          if (!projectId) return null;
          return await ProjectDataService.getDigitalizationProjectTitleById(
            projectId,
          );
        },
        getRouteParams: (route) => {
          const projectId = getParamFromRoute(route, PROJECT_ID_PARAM);
          if (!projectId) return null;
          const paramsConfig =
            routeParams[Route.DIGITALIZATION_PROJECT_DETAILS];
          return {
            name: Route.DIGITALIZATION_PROJECT_DETAILS,
            params: {
              [paramsConfig[PROJECT_ID_PARAM]]: projectId,
            },
          };
        },
        children: [
          {
            route: Route.CONTENT_TYPE_CONFIGURATION,
          },
          {
            route: Route.CHECK_BOT_CONFIGURATOR,
          },
          {
            route: Route.METADATA_GRID_EDITOR,
          },
          {
            route: Route.DOCUMENT_DETAIL_VIEW,
            getLabel: async (route) => {
              const documentOid = getParamFromRoute(route, DOCUMENT_OID_PARAM);
              if (!documentOid) return null;
              return await DocumentsService.getDigitalizationDocumentTitle(
                documentOid,
              );
            },
            getAdditionalPrecedingBreadcrumb: (
              route,
              t,
              historyPath,
              router,
            ) => {
              const uploadReferenceNumber = getParamFromRoute(
                route,
                UPLOAD_REFERENCE_NUMBER_PARAM,
              );
              const projectId = getParamFromRoute(route, PROJECT_ID_PARAM);
              if (!uploadReferenceNumber || !projectId || !t) return null;
              return {
                route: Route.DIGITALIZATION_PROJECT_DETAILS,
                getLabel: () =>
                  `${t(
                    'breadCrumb.uploadReferenceNumber',
                  )} ${uploadReferenceNumber}`,
                getRouteParams: () => {
                  const paramsFromHistory =
                    historyPath && router ? router.resolve(historyPath) : null;
                  const paramsConfig =
                    routeParams[Route.DIGITALIZATION_PROJECT_DETAILS];
                  return {
                    name: Route.DIGITALIZATION_PROJECT_DETAILS,
                    params: {
                      ...(paramsFromHistory ? paramsFromHistory.params : {}),
                      [paramsConfig[PROJECT_ID_PARAM]]: projectId,
                      [paramsConfig.tab]:
                        DigitalizationProjectDetailsTab.DOCUMENTS,
                    },
                    query: {
                      ...(paramsFromHistory ? paramsFromHistory.query : {}),
                      ...namespaceQueryParams(
                        {
                          referenceNumber: uploadReferenceNumber,
                        },
                        DIGITALIZATION_DOCUMENTS_TABLE_NAMESPACE,
                      ),
                    },
                  };
                },
              };
            },
          },
          {
            route: Route.DOCUMENT_UPLOAD,
          },
          {
            route: Route.UPLOAD_UPDATE,
          },
        ],
      },
    ],
  },
  {
    route: Route.WORKSPACES_LIST,
    children: [
      {
        route: Route.CREATE_WORKSPACE,
      },
      {
        route: Route.WORKSPACE_DETAILS,
        getLabel: async (route) => {
          const workspaceId = getParamFromRoute(route, WORKSPACE_ID_PARAM);
          if (!workspaceId) return null;
          const response = await axios.get<IWorkspaceDetailsDto>(
            endpointsList.workspaces.getWorkspaceById(workspaceId),
          );
          return response.data.name;
        },
        children: [
          {
            route: Route.WORKSPACE_GROUP_DETAILS,
            getLabel: async (route) => {
              const groupId = getParamFromRoute(
                route,
                WORKSPACE_GROUP_ID_PARAM,
              );
              if (!groupId) return null;
              const response = await axios.get<string>(
                endpointsList.workspaces.getWorkspaceGroupNameById(groupId),
              );
              return response.data;
            },
          },
          {
            route: Route.WORKSPACE_SINGLE_UPLOAD,
            getLabel: () =>
              (i18n.global as VueI18n).t(
                'workspaceDocuments.singleUploadTitle',
              ),
          },
          {
            route: Route.WORKSPACE_DOCUMENT_DETAIL_VIEW,
            getLabel: async (route) => {
              const workspaceId = getParamFromRoute(route, WORKSPACE_ID_PARAM);
              const documentId = getParamFromRoute(
                route,
                WORKSPACE_DOCUMENT_ID_PARAM,
              );
              if (!workspaceId || !documentId) return null;
              const accessMode = getWorkspaceDocumentDetailAccessMode(
                typeof route.query.documentsTab === 'string'
                  ? route.query.documentsTab
                  : undefined,
              );
              const response =
                await axios.get<IWorkspaceDocumentMetadataResponseDto>(
                  getWorkspaceDocumentDetailEndpoints(
                    accessMode,
                    workspaceId,
                    documentId,
                  ).metadata,
                );
              const titleField = response.data.metadata?.find(
                (field) => field.fieldName === TITLE_METADATA_FIELD,
              );
              return titleField?.fieldValue ?? null;
            },
          },
        ],
      },
    ],
  },
  {
    route: Route.SUPPLIERS_LIST,
    getRouteParams: () => ({
      name: Route.SUPPLIERS_LIST,
    }),
    children: [
      {
        route: Route.SUPPLIER_DETAILS,
      },
    ],
  },
  {
    route: Route.ADMINISTRATION,
  },
];

function getParamFromRoute(
  route: HistoryItem,
  param: string,
): string | undefined {
  if (typeof route.name !== 'string' || !(route.name in routeParams)) {
    return;
  }
  const currenRouteParamsConfig =
    routeParams[route.name as keyof typeof routeParams];
  const joinedRouteParams = {
    ...currenRouteParamsConfig,
    ...('query' in currenRouteParamsConfig
      ? currenRouteParamsConfig.query
      : {}),
  };
  if (!(param in joinedRouteParams)) {
    return;
  }
  const paramName =
    joinedRouteParams[param as keyof typeof currenRouteParamsConfig];
  return (
    (route.params[paramName] as string) ?? (route.query[paramName] as string)
  );
}

/**
 * Recursively finds the path to the given route in the breadcrumb hierarchy.
 */
export function getBreadcrumbPath(
  targetRoute: Route,
  tree = breadcrumbHierarchy,
): BreadcrumbHierarchyItem[] | null {
  for (const node of tree) {
    if (node.route === targetRoute) {
      return [node];
    }

    if (node.children) {
      const childPath = getBreadcrumbPath(targetRoute, node.children);
      if (childPath !== null) {
        return [node, ...childPath];
      }
    }
  }

  return null;
}

export function getRootNodeForRoute(route: HistoryItem): Route | undefined {
  const branch = getBreadcrumbBranchForRoute(route);
  return getBreadcrumbPath(route.name as Route, branch)?.[0]?.route;
}

export function getBreadcrumbBranchForRoute(
  route: HistoryItem,
): BreadcrumbHierarchyItem[] | undefined {
  if (typeof route.name !== 'string' || !(route.name in routeParams)) {
    return;
  }
  const paramConfig = routeParams[route.name as keyof typeof routeParams];
  const allParamsConfig = {
    ...paramConfig,
    ...('query' in paramConfig ? paramConfig.query : {}),
  };
  if (!(PROJECT_TYPE_PARAM in allParamsConfig)) return;
  const projectTypeParam = allParamsConfig[PROJECT_TYPE_PARAM];
  const projectType: ProjectType =
    (route.params[projectTypeParam] as ProjectType) ??
    (route.query[projectTypeParam] as ProjectType);
  switch (projectType) {
    case ProjectType.SUPPLIER_PROJECT:
      return [
        breadcrumbHierarchy.find((item) => item.route === Route.PROJECTS_LIST)!,
      ];
    case ProjectType.DIGITALIZATION_PROJECT:
      return [
        breadcrumbHierarchy.find(
          (item) => item.route === Route.DIGITALIZATION_PROJECTS_LIST,
        )!,
      ];
  }
}
